import * as React from 'react';

import { useSpecializationReviewsExperimentConfig } from 'bundles/unified-description-page-common/providers/specialization/SpecializationReviewsExperimentProvider';
import type { ConsumerSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationPageQuery';
import type { EnterpriseSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/EnterpriseSpecializationPageQuery';
import {
  MOCK_RATING_COUNTS_BY_STAR,
  getCourseIdToNameMap,
} from 'bundles/unified-description-page-common/utils/getMockSpecializationReviews';

type SpecializationPageData = ConsumerSpecializationPageFieldsFragment | EnterpriseSpecializationPageFieldsFragment;

/**
 * Data-fetching hook for the specialization reviews module. Forked from the course reviews
 * renderer's plain pageData read because specialization ratingCountsByStar isn't a real API
 * field yet (MERCH-3262) -- this mocks it onto the page data until it is, gated behind the
 * `specialization-reviews` Statsig experiment's `enableReviews` param. Top reviews come from the
 * real per-course reviews query (useCourseReviews) instead of being mocked here.
 */
export const useSpecializationReviewsPageData = (pageData: SpecializationPageData | null | undefined) => {
  const { showReviews } = useSpecializationReviewsExperimentConfig();

  return React.useMemo(() => {
    if (!showReviews || !pageData) {
      return {
        ...pageData,
        ratingCountsByStar: null,
        courseIdToNameMap: new Map<string, string>(),
      };
    }

    return {
      ...pageData,
      ratingCountsByStar: MOCK_RATING_COUNTS_BY_STAR,
      courseIdToNameMap: getCourseIdToNameMap(pageData.courses),
    };
  }, [showReviews, pageData]);
};
